// RecommendationDropdown.js - Dropdown panel showing catalog component recommendations
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import { getScoreQuality } from '../../utils/componentMatcher';
import RecommendationConfirmModal from './RecommendationConfirmModal';

const RecommendationDropdown = ({ nodeId, nodeType, anchorEl, onClose, onReplace }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [usedFrequency, setUsedFrequency] = useState(null);
  const [selectedRec, setSelectedRec] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const {
    fetchRecommendations,
    isLoadingRecommendations,
    recommendationError,
    nodes,
    showTutorial,
    tutorialStep,
    completeTutorialAction
  } = useStore();


  // Get the generic node's parameters for comparison
  const genericNode = nodes.find(n => n.id === nodeId);
  const genericParams = genericNode?.data?.parameters || {};

  // Calculate dropdown position
  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left - 120 // Center-ish the dropdown
      });
    }
  }, [anchorEl]);

  // Fetch recommendations on mount
  useEffect(() => {
    const loadRecs = async () => {
      try {
        const result = await fetchRecommendations(nodeId);
        setRecommendations(result.recommendations);
        setUsedFrequency(result.frequency);
      } catch (error) {
        // Error is already set in store by fetchRecommendations
        setRecommendations([]);
        setUsedFrequency(null);
      }
    };
    loadRecs();
  }, [nodeId, fetchRecommendations]);

  const handleRecommendationClick = (rec) => {
    setSelectedRec(rec);
  };

  const handleConfirmReplace = () => {
    if (selectedRec && onReplace) {
      onReplace(selectedRec);
      // Track for tutorial
      if (showTutorial) {
        completeTutorialAction('partReplaced');
      }
    }
    setSelectedRec(null);
    onClose();
  };

  const handleCancelReplace = () => {
    setSelectedRec(null);
  };

  // Drag handlers for movable modal
  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag when clicking buttons
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.left,
      y: e.clientY - position.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      left: e.clientX - dragOffset.x,
      top: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);


  // Render to portal for proper z-index
  return ReactDOM.createPortal(
    <>
      <Backdrop onClick={onClose} />
      <DropdownContainer style={{ top: position.top, left: position.left }}>
        <DropdownHeader onMouseDown={handleMouseDown} $isDragging={isDragging}>
          <HeaderTitle>Similar Catalog Parts</HeaderTitle>
          <HeaderSubtitle>@ {usedFrequency} MHz</HeaderSubtitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DropdownHeader>

        <DropdownBody>
          {isLoadingRecommendations && (
            <LoadingState>Loading recommendations...</LoadingState>
          )}

          {recommendationError && (
            <ErrorState>Error: {recommendationError}</ErrorState>
          )}

          {!isLoadingRecommendations && !recommendationError && recommendations.length === 0 && (
            <EmptyState>No matching catalog components found at this frequency.</EmptyState>
          )}

          {recommendations.map((rec) => {
            const quality = getScoreQuality(rec.matchScore);
            return (
              <RecommendationCard key={rec.partNumber} onClick={() => handleRecommendationClick(rec)}>
                <CardHeader>
                  <PartNumberRow>
                    <PartNumber>{rec.partNumber}</PartNumber>
                    <IccValue>{rec.interpolatedParams?.dcCurrent?.toFixed(0) ?? 'N/A'} mA</IccValue>
                  </PartNumberRow>
                  <ManufacturerRow>
                    <Manufacturer>{rec.manufacturer}</Manufacturer>
                    {rec.frequencyRange && (
                      <FreqRange>{rec.frequencyRange.min} - {rec.frequencyRange.max} MHz</FreqRange>
                    )}
                  </ManufacturerRow>
                </CardHeader>
                <CardParams>
                  <ParamItem>
                    <ParamLabel>Gain:</ParamLabel>
                    <ParamValue>{rec.interpolatedParams?.gain?.toFixed(1) ?? 'N/A'} dB</ParamValue>
                  </ParamItem>
                  <ParamItem>
                    <ParamLabel>NF:</ParamLabel>
                    <ParamValue>{rec.interpolatedParams?.noiseFigure?.toFixed(2) ?? 'N/A'} dB</ParamValue>
                  </ParamItem>
                  <ParamItem>
                    <ParamLabel>OP1dB:</ParamLabel>
                    <ParamValue>{rec.interpolatedParams?.p1db?.toFixed(1) ?? 'N/A'} dBm</ParamValue>
                  </ParamItem>
                  <ParamItem>
                    <ParamLabel>OIP3:</ParamLabel>
                    <ParamValue>{rec.interpolatedParams?.oip3?.toFixed(1) ?? 'N/A'} dBm</ParamValue>
                  </ParamItem>
                </CardParams>
                <CardFooter>
                  <ScoreBar>
                    <ScoreFill $score={rec.matchScore} $color={quality.color} />
                  </ScoreBar>
                  <ScoreText $color={quality.color}>{rec.matchScore}%</ScoreText>
                </CardFooter>
              </RecommendationCard>
            );
          })}
        </DropdownBody>
      </DropdownContainer>

      {selectedRec && (
        <RecommendationConfirmModal
          isOpen={!!selectedRec}
          onClose={handleCancelReplace}
          onConfirm={handleConfirmReplace}
          genericParams={genericParams}
          catalogComponent={selectedRec}
          frequency={usedFrequency}
        />
      )}
    </>,
    document.body
  );
};

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const DropdownContainer = styled.div`
  position: fixed;
  width: 280px;
  max-height: 400px;
  background: #2a2a2a;
  border: 1px solid #404040;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  z-index: 1000;
  overflow: hidden;
  animation: slideIn 0.15s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DropdownHeader = styled.div`
  position: relative;
  padding: 12px 16px;
  background: #1e1e1e;
  border-bottom: 1px solid #404040;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
`;

const HeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #e0e0e0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HeaderSubtitle = styled.div`
  font-size: 11px;
  color: #808080;
  margin-top: 2px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: #808080;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #e0e0e0;
  }
`;

const DropdownBody = styled.div`
  padding: 8px;
  max-height: 340px;
  overflow-y: auto;
`;

const LoadingState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #808080;
  font-size: 12px;
`;

const ErrorState = styled.div`
  padding: 16px;
  text-align: center;
  color: #f97316;
  font-size: 12px;
`;

const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #808080;
  font-size: 12px;
`;

const RecommendationCard = styled.div`
  background: #1e1e1e;
  border: 1px solid #363636;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #3b82f6;
    background: #252525;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 8px;
`;

const PartNumberRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PartNumber = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #ff6b6b;
  text-transform: uppercase;
`;

const IccValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #e0e0e0;
`;

const ManufacturerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Manufacturer = styled.div`
  font-size: 10px;
  color: #808080;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FreqRange = styled.div`
  font-size: 10px;
  color: #808080;
  font-weight: 600;
`;

const CardParams = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 12px;
  margin-bottom: 8px;
`;

const ParamItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ParamLabel = styled.span`
  font-size: 10px;
  color: #808080;
`;

const ParamValue = styled.span`
  font-size: 11px;
  color: #e0e0e0;
  font-weight: 500;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ScoreBar = styled.div`
  flex: 1;
  height: 6px;
  background: #363636;
  border-radius: 3px;
  overflow: hidden;
`;

const ScoreFill = styled.div`
  height: 100%;
  width: ${props => props.$score}%;
  background: ${props => props.$color};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ScoreText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$color};
  min-width: 36px;
  text-align: right;
`;

export default RecommendationDropdown;
